import { useCallback } from 'react'

import { MEMBER_PREFERENCE_NAMES, ProviderPreferenceValues } from '../../memberPreferences/constants'
import { MemberPreferencesForUserV2, ParsedPreferenceKey } from '../../memberPreferences/types'
import {
  getBipocUserPreferenceSelection,
  getProviderPreferenceKeyFromParsedKey,
  removePreferenceValueFromParsedPreferences,
} from '../../memberPreferences/utils'

export interface UseRemoveMatchedPreferenceArgs {
  memberPreferencesV2: MemberPreferencesForUserV2 | null
  /** Callback to save updated preferences. Callsite handles userId and Redux state updates. */
  onSavePreferences: (updatedPreferences: MemberPreferencesForUserV2) => void
  setCurrentPreferences?: React.Dispatch<React.SetStateAction<ProviderPreferenceValues>>
}

export const useRemoveMatchedPreference = ({
  memberPreferencesV2,
  onSavePreferences,
  setCurrentPreferences,
}: UseRemoveMatchedPreferenceArgs) => {
  return useCallback(
    ({ preferenceKey, valueId }: { preferenceKey: ParsedPreferenceKey; valueId: string | undefined }) => {
      if (!memberPreferencesV2) return

      // 1. Calculate updated preferences
      const basePreferences = removePreferenceValueFromParsedPreferences(
        memberPreferencesV2,
        preferenceKey,
        valueId,
      ) as MemberPreferencesForUserV2

      // Recalculate isBipoc when ethnicities are modified to maintain data consistency
      const updatedPreferences = {
        ...basePreferences,
        ...(preferenceKey === MEMBER_PREFERENCE_NAMES.ETHNICITIES && {
          isBipoc: getBipocUserPreferenceSelection({ userPreferenceSelections: basePreferences.ethnicities }),
        }),
      }

      // 2. Update local accordion state (if provided - used in custom preferences modal)
      if (setCurrentPreferences) {
        const targetKey = getProviderPreferenceKeyFromParsedKey(preferenceKey)

        setCurrentPreferences((prev) => {
          // Handle availabilities specially - empty state is null, not []
          if (preferenceKey === MEMBER_PREFERENCE_NAMES.AVAILABILITIES) {
            return { ...prev, [targetKey]: null }
          }
          const currentValue = prev[targetKey]
          if (!Array.isArray(currentValue)) return prev
          // When valueId is undefined, clear the entire preference
          if (!valueId) {
            return { ...prev, [targetKey]: [] }
          }
          return { ...prev, [targetKey]: currentValue.filter((v) => v !== valueId) }
        })
      }

      // 3. Save to backend and update Redux state (handled by callsite)
      onSavePreferences(updatedPreferences)
    },
    [memberPreferencesV2, onSavePreferences, setCurrentPreferences],
  )
}
